import * as Sentry from '@sentry/nextjs'
import { isProduction, enableSentry } from 'utils/env'
import type {
  SENTRY_DSN_ASK_CF,
  SENTRY_DSN_PAYMENTS,
  SENTRY_DSN_STUDIO,
  SENTRY_DSN_WEB,
  SENTRY_DSN_WEB_LANDING_PAGES,
} from '@repo/sentry-utils/constants'
import { clientIgnoreErrors, denyUrls } from '@repo/sentry-utils/constants'
import { beforeSend } from '@repo/sentry-utils/options'
import { configureSampleRate, sampleRateEventProcessor } from '@repo/sentry-utils/sample-rate'

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

export function initSentryNextSpecific({
  dsn,
  applicationKey,
}: {
  dsn:
    | typeof SENTRY_DSN_PAYMENTS
    | typeof SENTRY_DSN_STUDIO
    | typeof SENTRY_DSN_WEB
    | typeof SENTRY_DSN_WEB_LANDING_PAGES
    | typeof SENTRY_DSN_ASK_CF
  applicationKey:
    | 'cf-web/apps/web'
    | 'cf-web/apps/web-landing-pages'
    | 'cf-web/apps/ask-cf'
    | 'cf-web/apps/team'
  options?: Sentry.BrowserOptions
}) {
  // helps debug sentry with the sentry_debug query param
  const debug = new URLSearchParams(window.location.search).get('sentry_debug') !== null

  // Error sampling happens in sampleRateEventProcessor so the rate can change at runtime
  // (e.g. boosted to 100% for CF employees). The SDK's own sampling must not drop events first.
  // on production only send 20% of error events to stay under Sentry quota
  configureSampleRate(isProduction ? 0.2 : 1)

  Sentry.init({
    dsn,
    ignoreErrors: [
      ...clientIgnoreErrors,
      // These are errors that are caused by Cloudflare adding scripts in the source and are not useful to be reported to Sentry.
      'Hydration failed because the server rendered HTML',
      'There was an error while hydrating but React was able to recover',
      'Text content does not match server-rendered HTML.',
      //List of all errors: https://github.com/facebook/react/blob/main/scripts/error-codes/codes.json
      'Minified React error #418',
      'Minified React error #422',
      'Minified React error #423',
      'Minified React error #425',
      'https://reactjs.org/docs/error-decoder.html?invariant=418', //"Hydration failed because the server rendered HTML didn't match the client.",
      'https://reactjs.org/docs/error-decoder.html?invariant=422', //"There was an error while hydrating but React was able to recover by instead client rendering from the nearest Suspense boundary."
      'https://reactjs.org/docs/error-decoder.html?invariant=423', //"There was an error while hydrating but React was able to recover by instead client rendering the entire root."
      'https://reactjs.org/docs/error-decoder.html?invariant=425',
      "This is not a real error! It's an implementation detail of", //https://github.com/facebook/react/issues/27896
      "null is not an object (evaluating '_0xh.contentWindow.document')", //Added by Cloudflare
      "null is not an object (evaluating 'a.contentWindow.document')",
      'CF_ABORT_ERROR',
    ],
    denyUrls,
    enabled: enableSentry,
    environment: isProduction ? 'production' : 'development',

    // sampling is applied by sampleRateEventProcessor (see configureSampleRate above)
    sampleRate: 1,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/creativefabrica\.com/],

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug,

    replaysOnErrorSampleRate: 0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      // eslint-disable-next-line import/namespace
      Sentry.thirdPartyErrorFilterIntegration({
        // Specify the application keys that you specified in the Sentry bundler plugin
        filterKeys: [applicationKey],

        // Defines how to handle errors that contain third party stack frames.
        // Possible values are:
        // - 'drop-error-if-contains-third-party-frames'
        // - 'drop-error-if-exclusively-contains-third-party-frames'
        // - 'apply-tag-if-contains-third-party-frames'
        // - 'apply-tag-if-exclusively-contains-third-party-frames'
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    beforeSend,
  })

  Sentry.addEventProcessor(sampleRateEventProcessor)
}
